import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import CapitalEventDateForm from "features/capital-activity/event/CapitalEventDateForm";
import {
  getFundFamilyEventRetrieveQueryKey,
  useFundFamilyEventRetrieveSuspense,
  useFundFamilyEventUpdate,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { FundFamilyEventEditDataRequest } from "gen/model/fundFamilyEventEditDataRequest";
import { fundFamilyEventUpdateBody } from "gen/schemas/fund-family-event/fund-family-event";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormTextArea } from "shared/components/input/text/TextArea";

export interface CapitalEventEditDateModalProps {
  fundFamilyId: string;
  fundFamilyEventId: string;
  onCloseComplete(): void;
}

export const CapitalEventEditDateModal: React.FC<
  CapitalEventEditDateModalProps
> = ({ fundFamilyId, fundFamilyEventId, onCloseComplete }) => {
  const { t } = useTranslation(["capitalCall", "capitalActivity", "common"]);
  const queryClient = useQueryClient();

  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });

  const { data: capitalActivityData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const { mutateAsync: updateFundFamilyEvent } = useFundFamilyEventUpdate({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: getFundFamilyEventRetrieveQueryKey(
            fundFamilyId,
            fundFamilyEventId,
          ),
        });
      },
    },
  });

  const onSubmit = async (data: FundFamilyEventEditDataRequest) => {
    await updateFundFamilyEvent({
      fundFamilyId,
      fundFamilyEventId,
      data: {
        ...capitalActivityData,
        ...data,
      },
    });
  };

  const form = useForm<
    FundFamilyEventEditDataRequest & { different_capital_call_date: boolean }
  >({
    defaultValues: {
      effective_date: capitalActivityData.effective_date,
      notes: capitalActivityData.notes ?? "",
      posted_date: capitalActivityData.posted_date,
      capital_call_effective_date:
        capitalActivityData.capital_call_effective_date ?? null,
      capital_call_posted_date:
        capitalActivityData.capital_call_posted_date ?? null,
      notice_generation_celery_workflow_id:
        capitalActivityData.notice_generation_celery_workflow_id ?? null,
      distribution_withholding_upload_document_id:
        capitalActivityData.distribution_withholding_upload_document_id ?? null,
      celery_workflow_id: capitalActivityData.celery_workflow_id ?? null,
      different_capital_call_date:
        !!capitalActivityData.capital_call_effective_date ||
        !!capitalActivityData.capital_call_posted_date,
    },
    resolver: zodResolver(fundFamilyEventUpdateBody),
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <Form form={form} onSubmit={onSubmit} onSuccess={onClose}>
          <ModalHeader>
            {t("capitalCall:editModal.title")}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <Text marginBottom={4} fontSize={14} fontWeight={400}>
              {t("capitalCall:editModal.description")}
            </Text>
            <CapitalEventDateForm />
            <FormTextArea name="notes" title={t("capitalCall:notesOptional")} />
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={onClose} autoFocus={true} />}
              confirm={<FormSubmitButton>{t("common:save")}</FormSubmitButton>}
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
